import { createSelector } from 'reselect';
import LambdaUtil from '../../components/aws/lambda/lambdaUtil.js';
import { strings } from '../../localization.js';
import Permissions from '../permissions/index.js';
import { TAG_COLORS, TAG_TYPES, TAG_DEFAULT_STYLE, TAG_TYPES_BADGES, TAG_ROLES_ICONS, TAG_USER_ROLES } from './constants.js';

const NEW_TAG_TEMPORARY_UUID = undefined; // It is literally undefined. It is used to indicate that the tag is new and has not been assigned a UUID from the cloud

const REMOTE_ACTION_TYPES = {
    ADD: "ADD",
    UPDATE: "UPDATE",
    DELETE: "DELETE",
    GET: "GET"
}

const STATE_ACTION_TYPES = {

    SET_STAGE: "SET_STAGE", // used to set the current stage (i.e., development, production, etc.)

    SET_ALL_TAGS: "SET_ALL_TAGS", // used when loading/refresh all settings
    RESET_ALL: "RESET_ALL", // used when signing out

    SET_TAG: "SET_TAG",
    REMOVE_TAG: "REMOVE_TAG",
}

const TAG_TYPES_MAPPING = {
    SYSTEM: 1,
    LOCATION: 2,
    ORGANIZATION: 3,
    FACILITY: 4,
    EQUIPMENT: 5
}

const TAG_USER_ROLES_MAPPING = {
    OWNER: 0,
    ADMIN: 2,
    GUEST: 1
}

const TAG_USER_TYPES = {
    OWN_USER: 'OWN_USER',
    INHERITED_USER: 'INHERITED_USER',
    SHARED_USER: 'SHARED_USER'
}

const NEW_TAG_TEMPLATE = {
    editing: false,           // this tag data is being edited
    pending: false,           // this tag data is being loaded or is being changed or refreshed
    propChangePending: false, // legacy support for editing name independently, indicates that the name is being changed
    errorMessage: undefined,  // an error occurred that affects this tag (i.e., this tag could not be loaded)

    tagId: NEW_TAG_TEMPORARY_UUID,        // undefined until the tag is POSTed to the cloud
    parentTagId: undefined,
    name: strings.tags_new_tag_default_name,   // "Untitled",
    type: TAG_TYPES.EQUIPMENT,
    style: TAG_DEFAULT_STYLE[TAG_TYPES.EQUIPMENT],
    ancestors: [],
    users: [],
    ravens: [],
    isLeaf: true,
    isRoot: false
};

const INITIAL_STATE = { // used during initialization and RESET_ALL
    stage: null,
    pending: false, // a request that could change any tag data is in progress (i.e., show a spinner)
    errorMessage: undefined, // an error occurred that affects all tags (i.e., tags could not be loaded))
    inheritedUsersAreSet: false, // flag to indicate that the inherited users have been set for the tags

    list: undefined, // undefined means that the tags have not been loaded. Once load happens it will be an array of tags

};

const convertTagStyleStringToStyleObject = (tag) => {
    
    const defaultStyle = TAG_DEFAULT_STYLE[tag.type] ?? TAG_DEFAULT_STYLE[TAG_TYPES.SYSTEM];
    const tagStyle = tag.style;

    if(!tagStyle) return defaultStyle;

    if(typeof tagStyle === "object") return {...defaultStyle, ...tagStyle};

    try{
        try
        {
            
            let cleanedTagStyle = tagStyle.replace(/"/g, "^");
            
            cleanedTagStyle = cleanedTagStyle.replace(/'/g, '"');
            
            cleanedTagStyle = cleanedTagStyle.replace(/\^/g, "");
            
            const parsedStyle = JSON.parse(cleanedTagStyle);

            if(parsedStyle.color && !parsedStyle.color.startsWith("#")) parsedStyle.color = "#" + parsedStyle.color;

            return {...defaultStyle, ...parsedStyle};
        }
        catch(error){
            const parsedStyle = JSON.parse(tagStyle);

            if(parsedStyle.color && !parsedStyle.color.startsWith("#")) parsedStyle.color = "#" + parsedStyle.color;

            return {...defaultStyle, ...parsedStyle};
        }
    }
    catch{
        return defaultStyle;
    }
    
}

const getAncestors = (tag, tagsMap) => {
    
    // Function to find ancestors recursively
    const findAncestors = (tag, ancestors = []) => {
        if (!tag) return ancestors;
        ancestors.unshift(tag.tagId);
        return findAncestors(tagsMap[tag.parentTagId], ancestors);
    };
    

    const ancestors = findAncestors(tag);

    return ancestors;
}

const flagRootTags = (tags) => {
    tags.forEach(tag => {
        const parentTag = tags.find(t => t.tagId === tag.parentTagId);
        
        if(!parentTag){
            tag.isRootTag = true;
        }
    });

    return tags;
}

const setUsers = (users) => {

    // flag tag's own users
    const usersWithoutFlag = users.filter(user => !user.type);

    usersWithoutFlag.forEach(user => {
        user.type = TAG_USER_TYPES.OWN_USER;
    });


    // In app, we don't have use for OWNER role. We use ADMIN role instead.
    const ownerUser = users.find(user => user.role === TAG_USER_ROLES.OWNER);

    if(ownerUser){
        ownerUser.role = TAG_USER_ROLES.ADMIN;
    }

    return users;
}

const getUserRole = (role) => {
    if(!role) return;
    return role === TAG_USER_ROLES.OWNER ? TAG_USER_ROLES.ADMIN : role;
}

const getHighestRole = (...roles) => {

    if(roles.includes(TAG_USER_ROLES.ADMIN)){
        return TAG_USER_ROLES.ADMIN;
    }
    else if(roles.includes(TAG_USER_ROLES.GUEST)){
        return TAG_USER_ROLES.GUEST;
    }

    return null;
}

// takes an tag from the cloud or from local state and ensures that it has all the properties to be saved in local state.
const convertToLocalTag = (tag, tagsMap) => {
    
    const localTag = {
        editing: tag.hasOwnProperty("editing") ? tag.editing : NEW_TAG_TEMPLATE.editing,
        pending: tag.hasOwnProperty("pending") ? tag.pending : NEW_TAG_TEMPLATE.pending,
        propChangePending: tag.hasOwnProperty("propChangePending") ? tag.propChangePending : NEW_TAG_TEMPLATE.propChangePending,

        errorMessage: tag.errorMessage ? tag.errorMessage : NEW_TAG_TEMPLATE.errorMessage,

        tagId: tag.tagId,
        parentTagId: tag.parentTagId,
        ancestors: tag.ancestors ? tag.ancestors : getAncestors(tag, tagsMap),
        name: tag.name,
        type: tag.type,
        style: convertTagStyleStringToStyleObject(tag),

        users: tag.users ? setUsers(tag.users) : [],
        ravens: tag.ravens ? tag.ravens : [],

        isRootTag: tag.isRootTag,

        original: {
            name: tag.name, // original name for when updating a tag
            users: JSON.parse(JSON.stringify(tag.users)), // to compare with the changes in the users list when updating a tag
            ravens: JSON.parse(JSON.stringify(tag.ravens)) // to compare with the changes in the ravens list when updating a tag
        }
    };
    
    if(tag.hasOwnProperty("parent")) {
        localTag.parent = tag.parent;
    }

    return localTag;
};

const convertToRemoteTag = (tag, action = REMOTE_ACTION_TYPES.ADD) => {

    let remoteTag = {
        name: tag.name,
        type: String(TAG_TYPES_MAPPING[tag.type]),
        style: tag.style ? {color: tag.style.color} : {},
        users: {},
        ravens: {}
    };

    if(action === REMOTE_ACTION_TYPES.ADD) {

        const ownUsers = tag.users.filter(user => user.type !== TAG_USER_TYPES.INHERITED_USER);

        remoteTag.style = JSON.stringify(remoteTag.style);
        remoteTag.parent = tag.parent;
        remoteTag.users.toAdd = ownUsers.map(user => { return {userId: user.userId, role: String(TAG_USER_ROLES_MAPPING[user.role])}});
        remoteTag.ravens.toAdd = tag.ravens.map(raven => { return {ravenUuid: raven.ravenUuid}});
    }

    if(action === REMOTE_ACTION_TYPES.UPDATE) {
        remoteTag.style = JSON.stringify(remoteTag.style);
        remoteTag.originalName = tag.original.name;
    }

    return remoteTag;
};

// STATE_ACTION_CREATORS deviates from Modular Redux (Ducks) Rule #2: MUST export its action creators as functions (https://github.com/erikras/ducks-modular-redux)
// Here, we are using an exported object, STATE_ACTION_CREATORS, to group all action creators together similarly to STATE_ACTION_TYPES.
// To the best of my knowledge, this is not a requirement for Ducks, but it seems like a good practice to keep all action creators together without needing something like a naming convention
const STATE_ACTION_CREATORS = { // state update "actions" (i.e. each action takes new data to build a payload, which is used by a reducer as a "delta" to update the state)

    setStage: (stage) => {
        return {
            type: STATE_ACTION_TYPES.SET_STAGE,
            payload: {
                stage: stage
            }
        };
    },

    // updatingTags sets the state to indicate that tags data is being loaded or is being changed or refreshed
    updatingTags: () => {
        return {
            type: STATE_ACTION_TYPES.SET_ALL_TAGS, // what type of reducer action to take
            payload: { // payload delta to apply to state in the reducer
                pending: true, // this means "updating" is in progress
                errorMessage: undefined // error message is reset while changes are pending (i.e., any previous error was from a previous attempt)
            }
        }
    },

    // setTags sets the state to indicate that an attempt to load or change the data in the cloud was completed either successfully or with an error
    setTags: (tags = undefined, errorMessage = undefined, updatingProps = false) => {

        return {
            type: STATE_ACTION_TYPES.SET_ALL_TAGS,
            payload: {
                pending: false, // this means "updating" is not in progress, i.e. the data is set
                list: tags,
                errorMessage: errorMessage,
                updatingProps: updatingProps
            }
        }
    },

    // setTags sets the state to indicate that an attempt to load or change the data in the cloud was completed either successfully or with an error
    setTagsInheritedUsers: (tags) => {

        return {
            type: STATE_ACTION_TYPES.SET_ALL_TAGS,
            payload: {
                pending: false, // this means "updating" is not in progress, i.e. the data is set
                inheritedUsersAreSet: true,
                list: tags,
            }
        }
    },

    updatingTag: (tag) => {
        return {
            type: STATE_ACTION_TYPES.SET_TAG,
            payload: {
                tag: {
                    ...tag,
                    pending: true,
                    errorMessage: undefined
                }
            }
        }
    },

    updatingTagProp: (tag) => {
        return {
            type: STATE_ACTION_TYPES.SET_TAG,
            payload: {
                tag: {
                    ...tag,
                    propChangePending: true,
                    errorMessage: undefined
                }
            }
        }
    },

    setTag: (tag, errorMessage = undefined, replaceNewLocalTag = false) => {
        return {
            type: STATE_ACTION_TYPES.SET_TAG,
            payload: {
                tag: {
                    ...tag,
                    pending: false,
                    propChangePending: false,
                    errorMessage: errorMessage,
                },
                replaceNewLocalTag: replaceNewLocalTag
            }
        }
    },

    removeTag: (tagId) => {

        return {
            type: STATE_ACTION_TYPES.REMOVE_TAG,
            payload: {
                tag: {
                    tagId: tagId
                }
            }
        }
    },

    // resetAll sets the local data state back to INITIAL_STATE
    // useful during stuff like signing out
    resetAll: () => {
        return {
            type: STATE_ACTION_TYPES.RESET_ALL
        }
    },
};

// Modular Redux (Ducks) Rule #1: MUST export default a function called reducer() (https://github.com/erikras/ducks-modular-redux)
function tagsReducer (state = INITIAL_STATE, action) {

    // initiate scoped variables for the switch statement
    // this avoids declaring similar variables with unique names for each case
    let tagsFromState;
    let tagFromState;

    const tagsMergedMap = {}; // used to prevent duplicate tags and produce a merged list of unique tags

    let tagId;

    // TODO: as the list a shared variables grows, consider making helper functions for merging and cleaning data for similar ops below

    switch (action.type) {

        case STATE_ACTION_TYPES.SET_STAGE:
            return {
                ...state,
                stage: action.payload.stage
            };

        case STATE_ACTION_TYPES.SET_ALL_TAGS:

            if (!action.payload.list) { // check if this is a basic status update (i.e. pending to true) and no new tag data is being set
                return { // return a new state object which combines the existing state with the payload "delta" for the action type
                    ...state, // carry over everything else in state
                    pending: action.payload.pending,
                    errorMessage: action.payload.errorMessage
                };
            }

            
            if (action.payload.list && action.payload.updatingProps) { // check if this is a tags' prop update
                return { // return a new state object which combines the existing state with the payload "delta" for the action type
                    ...state, // carry over everything else in state
                    list: action.payload.list,
                    pending: action.payload.pending,
                    errorMessage: action.payload.errorMessage
                };
            }

            if (action.payload.list && action.payload.inheritedUsersAreSet) { 
                return { 
                    ...state,
                    list: action.payload.list,
                    pending: action.payload.pending,
                    inheritedUsersAreSet: action.payload.inheritedUsersAreSet
                };
            }

            // New tag data is being set.
            // Assumption: an all tags data update is for tags loaded from the cloud.
            // Therefore, merge the tag data with the existing state to preserve any local properties

            tagsFromState = state.tags ? state.tags : []; // tags are undefined on initial load
    
            // Use tagsFromState to preserve local values
            tagsFromState.forEach(tag => {
                tagsMergedMap[tag.tagId] = tag; // no need to clean the data because these are tags from the state (already cleaned)
            });

            const tagsAncestorsMap = {}; 
            action.payload.list.forEach(tag => {
                tagsAncestorsMap[tag.tagId] = tag;
            });

            action.payload.list.forEach(tag => {

                const mergedTag = convertToLocalTag(tag, tagsAncestorsMap);

                // initiate or carry-over the local state properties
                if (!tagsMergedMap[tag.tagId]) {
                    mergedTag.editing = NEW_TAG_TEMPLATE.editing;
                    mergedTag.pending = NEW_TAG_TEMPLATE.pending;
                    mergedTag.errorMessage = NEW_TAG_TEMPLATE.errorMessage;
                    mergedTag.propChangePending = NEW_TAG_TEMPLATE.propChangePending;
                } else {
                    mergedTag.editing = tagsMergedMap[tag.tagId].editing;
                    mergedTag.pending = tagsMergedMap[tag.tagId].pending;
                    mergedTag.errorMessage = tagsMergedMap[tag.tagId].errorMessage;
                    mergedTag.propChangePending = tagsMergedMap[tag.tagId].propChangePending;
                }

                tagsMergedMap[tag.tagId] = mergedTag;
            });

            // update the action payload with the cleaned data
            action.payload.list = Object.values(tagsMergedMap); // convert the map to an array of values

            return { // return a new state object which combines the existing state with the payload "delta" for the action type
                ...state, // carry over everything else in state
                ...action.payload // update the specific properties which were targetted and updated in the action/action-creator payload
            };
        
        case STATE_ACTION_TYPES.RESET_ALL:

            return {
                ...INITIAL_STATE
            };

        case STATE_ACTION_TYPES.SET_TAG:


            if (!action.payload.tag) {
                console.error("Data tagMonitoring reducer SET_TAG: tag is required");
                return {
                    ...state
                };
            }

            tagId = action.payload.tag.tagId;

            tagsFromState = state.list ? state.list : [];

            if (action.payload.replaceNewLocalTag) { // this flag is set on a successful response to an add-tag request (it will have the new tag's UUID from the cloud)

                tagsFromState = tagsFromState.map(tag => {
                    if (tag.tagId === NEW_TAG_TEMPORARY_UUID) {
                        tag.tagId = tagId; // assign the new tagId from the cloud after a successful add-tag request
                        delete tag.parent; // remove the parent property used for sending to the cloud
                    }
                    return tag;
                });
            }

            tagFromState = tagsFromState.find(tag => tag.tagId === tagId);

            if (!tagFromState) {

                // add new tag
                tagsFromState.push(convertToLocalTag(action.payload.tag));

                return {
                    ...state,
                    list: [...tagsFromState]
                }

            } else {
                // update existing tag
                return {
                    ...state,
                    list: tagsFromState.map(tag => { // update tag in the list
                        if (tag.tagId === tagId) {
                            return convertToLocalTag(action.payload.tag);
                        }
                        return tag;
                    })
                };    
            }

        case STATE_ACTION_TYPES.REMOVE_TAG:

            if (!action.payload.tag) {
                console.error("Data tagMonitoring reducer REMOVE_TAG: tag is required");
                return {
                    ...state
                };
            }

            tagId = action.payload.tag.tagId;

            tagsFromState = state.list ? state.list : [];

            return {
                ...state,
                list: tagsFromState.filter(tag => tag.tagId !== tagId)
            }

        default:
            return state;
    }
};

class TagsApi {

    static getTags(stage) {

        return LambdaUtil.getTags(stage)
        .then((response) => {
            const tags = response.tags;
            
            if(tags)
                return Promise.resolve(tags);
    
            return Promise.reject("No tags found");

        }).catch((err) => {
            return Promise.reject(err);
        });
    };

    static addTag(stage, tag) {

        const remoteTag = convertToRemoteTag(tag);

        return LambdaUtil.addTag(stage, remoteTag)
        .then((response) => {
            
            // Returning a random UUID because
            // 1- CRUD operations on tags are based on tag.name (and not tagId)
            // 2- The response from the cloud (at least now) does not return the new tag's UUID
            return Promise.resolve(crypto.randomUUID());

        }).catch((err) => {
            return Promise.reject(err);
        });
    };

    static updateTag(stage, tag) {

        const remoteTag = convertToRemoteTag(tag, REMOTE_ACTION_TYPES.UPDATE);

        return LambdaUtil.updateTag(stage, remoteTag)
        .then((response) => {
            return Promise.resolve(tag.tagId);

        }).catch((err) => {
            return Promise.reject(err);
        });
    }

    static addTagUser(stage, tag, user, role) {
        const roleId = String(TAG_USER_ROLES_MAPPING[role]);

        return LambdaUtil.addUserToTag(stage, tag, user.userId, roleId)
        .then((response) => {
            return Promise.resolve(tag.tagId);

        }).catch((err) => {
            return Promise.reject(err);
        });
    }

    static updateTagUser(stage, tag, user, role) {
        const roleId = String(TAG_USER_ROLES_MAPPING[role]);

        return LambdaUtil.updateUserRoleForTag(stage, tag, user.userId, roleId)
        .then((response) => {
            return Promise.resolve(tag.tagId);

        }).catch((err) => {
            return Promise.reject(err);
        });
    }

    static deleteTagUser(stage, tag, user) {
        return LambdaUtil.removeUserFromTag(stage, tag, user.userId)
        .then((response) => {
            return Promise.resolve(tag.tagId);

        }).catch((err) => {
            return Promise.reject(err);
        });
    }

    static addTagRaven(stage, tag, tagAncestors, raven) {
        return LambdaUtil.addRavenToTag(stage, tag, tagAncestors, raven.ravenUuid)
        .then((response) => {
            return Promise.resolve(tag.tagId);

        }).catch((err) => {
            return Promise.reject(err);
        });
    }
    
    static deleteTagRaven(stage, tag, tagDescendants, raven) {
        return LambdaUtil.removeRavenFromTag(stage, tag, tagDescendants, raven.ravenUuid)
        .then((response) => {
            return Promise.resolve(tag.tagId);

        }).catch((err) => {
            return Promise.reject(err);
        });
    }

    static deleteTag(stage, tag) {
        const remoteTag = convertToRemoteTag(tag, REMOTE_ACTION_TYPES.DELETE);

        return LambdaUtil.removeTag(stage, remoteTag)
        .then((response) => {
            return Promise.resolve(tag.tagId);

        }).catch((err) => {
            return Promise.reject(err);
        });
    };

}

const DispatchHelpers = {

    setStage: (stage) => (dispatch, getState) => {
        dispatch(STATE_ACTION_CREATORS.setStage(stage));
    },

    resetTags: () => (dispatch) => {
        dispatch(STATE_ACTION_CREATORS.resetAll());
        return Promise.resolve();
    },

    loadTags: () => (dispatch, getState) => { // redux-thunk function pattern (dispatch and getState are provided by redux-thunk)
        const state = getState();

        const stage = Selectors.stage(state);
        
        if(!stage) {
            console.error("Data tags loadTags: stage is required");
            return Promise.reject("stage is required");
        }

        const alreadyLoaded = Selectors.loaded(state);
        const loading = Selectors.loading(state);
        const error = Selectors.error(state);

        if (alreadyLoaded || loading || error) {
            return Promise.resolve(Selectors.rawTags(state));
        }

        dispatch(STATE_ACTION_CREATORS.updatingTags()); // sets a pending flag, resets error message

        return TagsApi.getTags(stage)
        .then(
            (tags) => {
                flagRootTags(tags);

                dispatch(STATE_ACTION_CREATORS.setTags(tags));

                return tags;
            },
            (error) => {
                const errorMessage = error.message ? error.message : "An error occurred";
                dispatch(STATE_ACTION_CREATORS.setTags(undefined, errorMessage));
                return Promise.reject(error);
            }
        );
    },

    setInheritedUsers: (users) => (dispatch, getState) => {
        const tags = Selectors.rawTags(getState());
        
        if(!tags) return;

        tags.forEach(tag => {
            const usersWithThisTag = users.filter(user => user.tags.some(userTag => userTag.tagId === tag.tagId));

            usersWithThisTag.forEach(user => {

                const tagUser = tag.users.find(tagUser => tagUser.email === user.email);
                const userTag = user.tags.find(userTag => userTag.tagId === tag.tagId);
                // User is not already in the tag's users list (i.e., not an own user)
                if(!tagUser) {
                    tag.users.push({
                        email: user.email,
                        userId: user.userId,
                        type: TAG_USER_TYPES.INHERITED_USER,
                        inheritedRole: getUserRole(userTag.role)
                    });
                }
                else {
                    
                    // if tag's parent tag has the same user, then the user is also inherited (i.e., a shared user)
                    const userIsInParentTag = user.tags.find(userTag => userTag.tagId === tag.parentTagId);
                    if(userIsInParentTag) {
                        tagUser.type = TAG_USER_TYPES.SHARED_USER;
                        tagUser.inheritedRole = getUserRole(userIsInParentTag.role);
                    }
                    //Else the user is an own user
                }
            });
        });

        dispatch(STATE_ACTION_CREATORS.setTagsInheritedUsers(tags));
    },

    updateTagName: (tagId, name) => (dispatch, getState) => {
        const state = getState();
        const tag = Selectors.tag(state, tagId);
        const stage = Selectors.stage(state);

        if (!tag) {
            console.error("Data tags updateTagName: tag not found");
            return Promise.reject("Tag not found");
        }

        if (!stage) {
            console.error("Data tags updateTagName: stage is required");
            return Promise.reject("Stage not found");
        }

        dispatch(STATE_ACTION_CREATORS.updatingTagProp(tag)); // sets a pending flag, resets error message

        const tagToUpdate = {
            ...tag,
            name: name
        };
        
        return TagsApi.updateTag(stage, tagToUpdate)
            .then(
                (response) => {
                    tag.name = name;
                    dispatch(STATE_ACTION_CREATORS.setTag(tag));
                    Promise.resolve(tagId);
                },
                (error) => {
                    const errorMessage = error.message ? error.message : "An error occurred";
                    dispatch(STATE_ACTION_CREATORS.setTag(tag, errorMessage));
                    return Promise.reject(error);
                }
            );
    },

    updateTagType: (tagId, tagType) => (dispatch, getState) => {
        const state = getState();
        const tag = Selectors.tag(state, tagId);
        const stage = Selectors.stage(state);
        
        if (!tag) {
            console.error("Data tags updateTagType: tag not found");
            return Promise.reject("Tag not found");
        }

        if (!stage) {
            console.error("Data tags updateTagType: stage is required");
            return Promise.reject("Stage not found");
        }

        dispatch(STATE_ACTION_CREATORS.updatingTagProp(tag)); // sets a pending flag, resets error message

        const tagToUpdate = {
            ...tag,
            type: tagType,
        };
        
        return TagsApi.updateTag(stage, tagToUpdate)
            .then(
                (response) => {
                    tag.type = tagType;
                    dispatch(STATE_ACTION_CREATORS.setTag(tag));
                    Promise.resolve(tagId);
                },
                (error) => {
                    const errorMessage = error.message ? error.message : "An error occurred";
                    dispatch(STATE_ACTION_CREATORS.setTag(tag, errorMessage));
                    return Promise.reject(error);
                }
            );
    },

    updateTagColor: (tagId, color) => (dispatch, getState) => {
        const state = getState();
        const tag = Selectors.tag(state, tagId);
        const stage = Selectors.stage(state);        
        
        if (!tag) {
            console.error("Data tags updateTagColor: tag not found");
            return Promise.reject("Tag not found");
        }

        if (!stage) {
            console.error("Data tags updateTagColor: stage is required");
            return Promise.reject("Stage not found");
        }

        dispatch(STATE_ACTION_CREATORS.updatingTagProp(tag)); // sets a pending flag, resets error message

        const tagToUpdate = {
            ...tag,
            style: {
                ...tag.style,
                color: color
            }
        };
        
        return TagsApi.updateTag(stage, tagToUpdate)
            .then(
                (response) => {
                    tag.style.color = color;
                    dispatch(STATE_ACTION_CREATORS.setTag(tag));
                    Promise.resolve(tagId);
                },
                (error) => {
                    const errorMessage = error.message ? error.message : "An error occurred";
                    dispatch(STATE_ACTION_CREATORS.setTag(tag, errorMessage));
                    return Promise.reject(error);
                }
            );
    },

    addNewTag: (parentTagId) => (dispatch, getState) => { // redux-thunk

        if(!parentTagId) {
            console.error("Data tags addNewTag: parentTagId is required");
            return Promise.reject("parentTag is required");
        }

        const newTag = {
            ...NEW_TAG_TEMPLATE,
            users: [],
            ravens: [],
            editing: true
        };


        let parentTag = Selectors.tag(getState(), parentTagId);

        if(parentTag) {
            newTag.parentTagId = parentTag.tagId;
            newTag.type = parentTag.type !== TAG_TYPES.SYSTEM ? parentTag.type : TAG_TYPES.EQUIPMENT;
            newTag.style = parentTag.type !== TAG_TYPES.SYSTEM ? parentTag.style : TAG_DEFAULT_STYLE.EQUIPMENT;
            newTag.ancestors = parentTag.ancestors;

            parentTag.users.forEach(user => {
                newTag.users.push({
                    email: user.email,
                    userId: user.userId,
                    role: user.role,
                    type: TAG_USER_TYPES.INHERITED_USER,
                    inheritedRole: getHighestRole(user.inheritedRole, user.role)
                });
            });
        }

        dispatch(STATE_ACTION_CREATORS.setTag(newTag)); // add the new tag to the local state (it will have a temporary UUID until it is saved to the cloud);
    },

    saveTagEdit: (editedTag) => (dispatch, getState) => {
        const state = getState();
        const tagFromState = Selectors.tag(state, editedTag.tagId);
        const stage = Selectors.stage(state);

        if (!tagFromState) {
            console.error("Data tags saveTagEdit: tag not found");
            return Promise.reject("Tag not found");
        }

        if (!stage) {
            console.error("Data tags saveTagEdit: stage is required");
            return Promise.reject("Stage is required");
        }

        dispatch(STATE_ACTION_CREATORS.updatingTags()); // sets a pending flag, resets error message
        dispatch(STATE_ACTION_CREATORS.updatingTag(editedTag)); // sets a pending flag, resets error message

        if (editedTag.tagId === NEW_TAG_TEMPORARY_UUID) {
            editedTag.parent = Selectors.tag(state, editedTag.parentTagId)?.name;

            return TagsApi.addTag(stage, editedTag)
                .then(
                    (addedTagUuid) => {
                        editedTag.tagId = addedTagUuid;
                        editedTag.editing = false;

                        const replaceNewLocalTag = true;

                        dispatch(STATE_ACTION_CREATORS.setTags());
                        dispatch(STATE_ACTION_CREATORS.setTag(editedTag, undefined, replaceNewLocalTag));

                        // Add permission for the new tag
                        const newTagPermissions = { self: { read: true, write: true } };
                        dispatch(Permissions.thunks.addResourcePermission('tags', addedTagUuid, newTagPermissions));

                        return Promise.resolve(editedTag);
                    },
                    (error) => {
                        editedTag.editing = true;
                        const errorMessage = error.message ? error.message : "An error occurred";
                        dispatch(STATE_ACTION_CREATORS.setTags());
                        dispatch(STATE_ACTION_CREATORS.setTag(editedTag, errorMessage));
                        return Promise.reject(error);
                    }
                );
        }

        return TagsApi.updateTag(stage, editedTag)
            .then(
                () => {
                    editedTag.editing = false;
                    dispatch(STATE_ACTION_CREATORS.setTags());
                    dispatch(STATE_ACTION_CREATORS.setTag(editedTag));
                    return Promise.resolve(editedTag);
                },
                (error) => {
                    const errorMessage = error.message ? error.message : "An error occurred";
                    dispatch(STATE_ACTION_CREATORS.setTags());
                    dispatch(STATE_ACTION_CREATORS.setTag(editedTag, errorMessage));
                    return Promise.reject(error);
                }
            );
    },

    startTagEdit: (tagId) => (dispatch, getState) => {
        const state = getState();
        const tag = Selectors.tag(state, tagId);

        if (!tag) {
            console.error("Data tags startTagEdit: tag not found");
            return Promise.reject("Tag not found");
        }

        dispatch(STATE_ACTION_CREATORS.setTag({
            ...tag,
            editing: true
        }));
    },

    cancelTagEdit: (tagId) => (dispatch, getState) => {
        // Check if there is an existing tag or a new tag in editing mode
        let tag = Selectors.editingTag(getState());

        // No editing or new tag found
        if (!tag) {
            console.error("Data tagMonitoring cancelTagEdit: tag not found");
            return Promise.reject("Tag not found");
        }
        
        if (tag.tagId === NEW_TAG_TEMPORARY_UUID) {
            dispatch(STATE_ACTION_CREATORS.removeTag(NEW_TAG_TEMPORARY_UUID)); // remove the new tag from the local state
        } else {
            dispatch(STATE_ACTION_CREATORS.setTag({
                ...tag,
                editing: false
            }));
        }

        return Promise.resolve();
    },

    deleteTag: (tagId) => (dispatch, getState) => {
        
        if (!tagId) {
            console.error("Data tags deleteTag: tagId is required");
            return Promise.reject("tagId is required");
        }

        const state = getState();
        const tagFromState = Selectors.tag(state, tagId);
        const stage = Selectors.stage(state);

        if (!tagFromState) {
            console.error("Data tags deleteTag: tag not found in state");
            return Promise.reject("Tag not found");
        }

        if (!stage) {
            console.error("Data tags deleteTag: stage is required");
            return Promise.reject("Stage is required");
        }

        dispatch(STATE_ACTION_CREATORS.updatingTags()); // sets a pending flag, resets error message
        dispatch(STATE_ACTION_CREATORS.updatingTag(tagFromState)); // sets a pending flag, resets error message

        return TagsApi.deleteTag(stage, tagFromState)
        .then(
            () => {
                dispatch(STATE_ACTION_CREATORS.setTags());

                // remove the tag from the local state
                dispatch(STATE_ACTION_CREATORS.removeTag(tagId));

                // Remove permission for the deleted tag
                dispatch(Permissions.thunks.removeResourcePermission('tags', tagId));
                
                return Promise.resolve(tagFromState);
            },
            (error) => {
                const errorMessage = error.message ? error.message : "An error occurred";
                dispatch(STATE_ACTION_CREATORS.setTags());
                dispatch(STATE_ACTION_CREATORS.setTag(tagFromState, errorMessage));
                return Promise.reject(error);
            }
        );
    },
    
    deleteTagUser: (tagId, user) => (dispatch, getState) => {
        
        if (!tagId) {
            console.error("Data tags deleteTagUser: tagId is required");
            return Promise.reject("tagId is required");
        }

        if (!user) {
            console.error("Data tags deleteTagUser: user is required");
            return Promise.reject("user is required");
        }

        const state = getState();
        const tagFromState = Selectors.tag(state, tagId);
        const stage = Selectors.stage(state);

        if (!tagFromState) {
            console.error("Data tags deleteTag: tag not found in state");
            return Promise.reject("Tag not found");
        }

        if (!stage) {
            console.error("Data tags deleteTagUser: stage is required");
            return Promise.reject("Stage is required");
        }

        dispatch(STATE_ACTION_CREATORS.updatingTags()); // sets a pending flag, resets error message
        dispatch(STATE_ACTION_CREATORS.updatingTagProp(tagFromState)); // sets a pending flag, resets error message

        return TagsApi.deleteTagUser(stage, tagFromState, user)
        .then(
            () => {
                tagFromState.users = tagFromState.users.filter(tagUser => tagUser.userId !== user.userId);

                dispatch(STATE_ACTION_CREATORS.setTag(tagFromState));

                const tags = Selectors.rawTags(state);

                const subTags = Selectors.tagDescendants(state, tagId);
                subTags.sort((a, b) => parseInt(a.tagId) - parseInt(b.tagId));

                subTags.forEach(subTag => {
                    subTag.users = subTag.users.map(tagUser => {
                        if (tagUser.userId === user.userId) {
                            const parentTag = tags.find(t => t.tagId === subTag.parentTagId);

                            if (parentTag) {
                                const parentTagUser = parentTag.users.find(ptu => ptu.userId === user.userId);
                                if (parentTagUser) {
                                    tagUser.inheritedRole = getHighestRole(parentTagUser.role, parentTagUser.inheritedRole);
                                } else {
                                    if (tagUser.type === TAG_USER_TYPES.INHERITED_USER) {
                                        return null;
                                    }
                                    if (tagUser.type === TAG_USER_TYPES.SHARED_USER) {
                                        tagUser.type = TAG_USER_TYPES.OWN_USER;
                                        tagUser.inheritedRole = null;
                                    }
                                }
                            }
                        }

                        return tagUser;
                    }).filter(tagUser => tagUser);

                    dispatch(STATE_ACTION_CREATORS.setTag(subTag));
                });

                dispatch(STATE_ACTION_CREATORS.setTags());

                return Promise.resolve(tagFromState);
            },
            (error) => {
                const errorMessage = error.message ? error.message : "An error occurred";
                dispatch(STATE_ACTION_CREATORS.setTags());
                dispatch(STATE_ACTION_CREATORS.setTag(tagFromState, errorMessage));
                return Promise.reject(error);
            }
        );
    },

    addTagUser: (tagId, user, role) => (dispatch, getState) => {
        
        if (!tagId) {
            console.error("Data tags addTagUser: tagId is required");
            return Promise.reject("tagId is required");
        }

        if (!user) {
            console.error("Data tags addTagUser: user is required");
            return Promise.reject("user is required");
        }

        if (!role) {
            console.error("Data tags addTagUser: role is required");
            return Promise.reject("role is required");
        }

        const state = getState();
        const tagFromState = Selectors.tag(state, tagId);
        const stage = Selectors.stage(state);

        if (!tagFromState) {
            console.error("Data tags addTagUser: tag not found in state");
            return Promise.reject("Tag not found");
        }

        if (!stage) {
            console.error("Data tags addTagUser: stage is required");
            return Promise.reject("Stage is required");
        }

        dispatch(STATE_ACTION_CREATORS.updatingTags()); // sets a pending flag, resets error message
        dispatch(STATE_ACTION_CREATORS.updatingTagProp(tagFromState)); // sets a pending flag, resets error message

        return TagsApi.addTagUser(stage, tagFromState, user, role)
        .then(
            () => {

                const newUser = {
                    email: user.email, 
                    userId: user.userId, 
                    role: role,
                    type: TAG_USER_TYPES.OWN_USER
                };
                
                tagFromState.users = [...tagFromState.users, newUser];

                dispatch(STATE_ACTION_CREATORS.setTag(tagFromState));

                const subTags = Selectors.tagDescendants(state, tagId);

                subTags.forEach(subTag => {
                    const tagUsers = subTag.users;

                    const userInSubTag = tagUsers.find(tagUser => tagUser.userId === user.userId);

                    if(userInSubTag) {
                        if(userInSubTag.type === TAG_USER_TYPES.OWN_USER) {
                            userInSubTag.type = TAG_USER_TYPES.SHARED_USER;
                        }
                        userInSubTag.inheritedRole = getHighestRole(role, userInSubTag.inheritedRole);
                    }
                    else {
                        tagUsers.push({
                            email: user.email, 
                            userId: user.userId, 
                            role: role,
                            type: TAG_USER_TYPES.INHERITED_USER,
                            inheritedRole: role
                        });
                    }

                    subTag.users = tagUsers;

                    dispatch(STATE_ACTION_CREATORS.setTag(subTag));
                });

                dispatch(STATE_ACTION_CREATORS.setTags());

                return Promise.resolve(tagFromState);
            },
            (error) => {
                const errorMessage = error.message ? error.message : "An error occurred";
                dispatch(STATE_ACTION_CREATORS.setTags());
                dispatch(STATE_ACTION_CREATORS.setTag(tagFromState, errorMessage));
                return Promise.reject(error);
            }
        );
    },

    updateTagUser: (tagId, user, role) => (dispatch, getState) => {
        
        if (!tagId) {
            console.error("Data tags updateTagUser: tagId is required");
            return Promise.reject("tagId is required");
        }

        if (!user) {
            console.error("Data tags updateTagUser: user is required");
            return Promise.reject("user is required");
        }

        if (!role) {
            console.error("Data tags updateTagUser: role is required");
            return Promise.reject("role is required");
        }

        const state = getState();
        const tagFromState = Selectors.tag(state, tagId);
        const stage = Selectors.stage(state);

        if (!tagFromState) {
            console.error("Data tags updateTagUser: tag not found in state");
            return Promise.reject("Tag not found");
        }

        if (!stage) {
            console.error("Data tags updateTagUser: stage is required");
            return Promise.reject("Stage is required");
        }

        dispatch(STATE_ACTION_CREATORS.updatingTags()); // sets a pending flag, resets error message
        dispatch(STATE_ACTION_CREATORS.updatingTagProp(tagFromState)); // sets a pending flag, resets error message

        return TagsApi.updateTagUser(stage, tagFromState, user, role)
        .then(
            () => {

                // update user role in the local tag.users list
                const updatedTagUser = tagFromState.users.find(tagUser => tagUser.userId === user.userId);

                updatedTagUser.role = role;
                // User role is updated manually, so it is no longer just inherited
                if(updatedTagUser.type === TAG_USER_TYPES.INHERITED_USER) {
                    updatedTagUser.type = TAG_USER_TYPES.SHARED_USER;
                }

                dispatch(STATE_ACTION_CREATORS.setTag(tagFromState));

                const tags = Selectors.rawTags(state);
                const subTags = Selectors.tagDescendants(state, tagId);

                subTags.sort((a, b) => parseInt(a.tagId) - parseInt(b.tagId));

                subTags.forEach(subTag => {
                    subTag.users = subTag.users.map(tagUser => {
                        if(tagUser.userId === user.userId) {
                            const parentTag = tags.find(tag => tag.tagId === subTag.parentTagId);
                            
                            if(parentTag) {
                                const parentTagUser = parentTag.users.find(parentTagUser => parentTagUser.userId === user.userId);
                                if(parentTagUser) {
                                    tagUser.inheritedRole = getHighestRole(parentTagUser.inheritedRole, parentTagUser.role);
                                }
                            }
                        }

                        return tagUser;
                    });

                    dispatch(STATE_ACTION_CREATORS.setTag(subTag));
                });

                dispatch(STATE_ACTION_CREATORS.setTags());

                return Promise.resolve(tagFromState);
            },
            (error) => {
                const errorMessage = error.message ? error.message : "An error occurred";
                dispatch(STATE_ACTION_CREATORS.setTags());
                dispatch(STATE_ACTION_CREATORS.setTag(tagFromState, errorMessage));
                return Promise.reject(error);
            }
        );
    },

    deleteTagRaven: (tagId, raven) => (dispatch, getState) => {
        
        if (!tagId) {
            console.error("Data tags deleteTagRaven: tagId is required");
            return Promise.reject("tagId is required");
        }

        if (!raven) {
            console.error("Data tags deleteTagRaven: raven is required");
            return Promise.reject("raven is required");
        }

        const state = getState();
        const tagFromState = Selectors.tag(state, tagId);
        const stage = Selectors.stage(state);

        if (!tagFromState) {
            console.error("Data tags deleteTagRaven: tag not found in state");
            return Promise.reject("Tag not found");
        }

        if (!stage) {
            console.error("Data tags deleteTagRaven: stage is required");
            return Promise.reject("Stage is required");
        }

        // A raven removed from a tag, must also be removed from the tag's descendants
        // So, get all the tags that have the tagId as an ancestor
        const tagDescendants = Selectors.tagDescendants(state, tagId);

        dispatch(STATE_ACTION_CREATORS.updatingTags()); // sets a pending flag, resets error message
        dispatch(STATE_ACTION_CREATORS.updatingTagProp(tagFromState)); // sets a pending flag, resets error message

        return TagsApi.deleteTagRaven(stage, tagFromState, tagDescendants, raven)
        .then(
            () => {

                const updatedTags = [tagFromState, ...tagDescendants];

                updatedTags.forEach(updatedTag => {

                    // remove the raven from the local tag.ravens list
                    updatedTag.ravens = updatedTag.ravens.filter(tagRaven => tagRaven.ravenUuid !== raven.ravenUuid);

                    dispatch(STATE_ACTION_CREATORS.setTag(updatedTag));
                });

                dispatch(STATE_ACTION_CREATORS.setTags());

                return Promise.resolve(updatedTags);
            },
            (error) => {
                const errorMessage = error.message ? error.message : "An error occurred";
                dispatch(STATE_ACTION_CREATORS.setTags());
                dispatch(STATE_ACTION_CREATORS.setTag(tagFromState, errorMessage));
                return Promise.reject(error);
            }
        );
    },

    addTagRaven: (tagId, raven) => (dispatch, getState) => {
        
        if (!tagId) {
            console.error("Data tags addTagRaven: tagId is required");
            return Promise.reject("tagId is required");
        }

        if (!raven) {
            console.error("Data tags addTagRaven: raven is required");
            return Promise.reject("raven is required");
        }

        const state = getState();
        const tagFromState = Selectors.tag(state, tagId);
        const stage = Selectors.stage(state);

        if (!tagFromState) {
            console.error("Data tags addTagRaven: tag not found in state");
            return Promise.reject("Tag not found");
        }

        if (!stage) {
            console.error("Data tags addTagRaven: stage is required");
            return Promise.reject("Stage is required");
        }

        const tagAncestors = Selectors.tagAncestors(state, tagId);
        const tagAncestorsWithoutTheRaven = tagAncestors.filter(tag => !tag.ravens.find(tagRaven => tagRaven.ravenUuid === raven.ravenUuid));


        dispatch(STATE_ACTION_CREATORS.updatingTags()); // sets a pending flag, resets error message
        dispatch(STATE_ACTION_CREATORS.updatingTagProp(tagFromState)); // sets a pending flag, resets error message

        return TagsApi.addTagRaven(stage, tagFromState, tagAncestorsWithoutTheRaven, raven)
        .then(
            () => {

                const newRaven = {ravenUuid: raven.ravenUuid};

                const updatedTags = [tagFromState, ...tagAncestorsWithoutTheRaven];

                updatedTags.forEach(updatedTag => {
                    // add the raven to the local tag.ravens list
                    updatedTag.ravens = [...updatedTag.ravens, newRaven];

                    dispatch(STATE_ACTION_CREATORS.setTag(updatedTag));
                });
                
                dispatch(STATE_ACTION_CREATORS.setTags());

                return Promise.resolve(updatedTags);

            },
            (error) => {
                const errorMessage = error.message ? error.message : "An error occurred";
                dispatch(STATE_ACTION_CREATORS.setTags());
                dispatch(STATE_ACTION_CREATORS.setTag(tagFromState, errorMessage));
                return Promise.reject(error);
            }
        );
    },
}

const Selectors = {

    stage: (state) => {
        return state.data.tags.stage;
    },

    loaded: (state) => {
        return state.data.tags.list !== undefined;
    },

    loading: (state) => {
        return state.data.tags.list === undefined && state.data.tags.pending === true;
    },

    pending: (state) => {
        return state.data.tags.pending;
    },

    error: (state) => {
        return state.data.tags.errorMessage;
    },

    tagEditModeActive: (state) => {
        return !!state.data.tags.list.find(tag => tag.editing === true);
    },

    editingTag: (state) => {
        const tags = Selectors.tags(state);
        if(!tags) return undefined;

        return tags.find(tag => tag.editing === true);
    },

    tagPropChangePending: (state) => {
        if(!state.data.tags.list) return false;

        const tag = state.data.tags.list.find(tag => tag.propChangePending === true);
        return tag ? true : false;
    },

    tagLoadOrSavePending: (state, tagId) => {
        const tag = state.data.tags.list.find(tag => tag.tagId === tagId);

        if (tag) return tag.pending;

        return false;
    },

    tagIds: (state) => {
        return state.data.tags.list.map(tag => tag.tagId);
    },

    tag: (state, tagId) => {
        return Selectors.tags(state)?.find(tag => tag.tagId === tagId);
    },

    rawTags: (state) => {
        return state.data.tags.list;
    },

    tags: createSelector(
        [state => state.data.tags.list, Permissions.selectors.map],
        (tags, permissions) => {
            if (!tags || !tags.length) {
                return tags;
            }

            return tags.map(tag => {
                const { matchedFilters, ...rest } = tag;

                return {
                    ...rest,
                    canEdit: Permissions.checkPermission(permissions, {resource: 'tags.self', action: 'write', context: { resourceId: tag.tagId }})
                };
            });
        }
    ),

    tagName: (state, tagId) => {
        const tag = state.data.tags.list.find(tag => tag.tagId === tagId);
        return tag ? tag.name : undefined;
    },

    inheritedUsersAreSet: (state) => {
        return state.data.tags.inheritedUsersAreSet;
    },

    tagDescendants: (state, tagId) => {
        const tags = Selectors.tags(state);
        if (!tags) return [];

        return tags.filter(tag => tag.ancestors.includes(tagId) && tag.tagId !== tagId);
    },

    tagAncestors: (state, tagId) => {
        const tags = Selectors.tags(state);
        if (!tags) return [];

        const tag = tags.find(t => t.tagId === tagId);
        if (!tag || !tag.ancestors) return [];

        return tags.filter(t => tag.ancestors.includes(t.tagId) && t.tagId !== tagId);
    },
};

export default tagsReducer;

export {
    TagsApi,
    DispatchHelpers,
    Selectors,
    TAG_COLORS, 
    TAG_TYPES, 
    TAG_DEFAULT_STYLE, 
    TAG_TYPES_BADGES, 
    TAG_ROLES_ICONS, 
    TAG_USER_ROLES,
    TAG_USER_TYPES,
    convertTagStyleStringToStyleObject
}

